<script setup lang="ts">
	import { nextTick, onMounted, reactive, ref } from "vue";
	import { useMap, useMapDrawing } from "@ptx-autonomous/libs/maplibre";
	import Dialog from "@/components/Dialog.vue";
	import BrushIcon from "@/icons/BrushIcon.vue";
	import DeleteIcon from "@/icons/DeleteIcon.vue";
	import EraserIcon from "@/icons/EraserIcon.vue";
	import PenIcon from "@/icons/PenIcon.vue";
	import SelectIcon from "@/icons/SelectIcon.vue";
	import { useValidation } from "@ptx-autonomous/libs/forms";
	import z from "zod";
	import { useFlightpathServiceHttp } from "@ptx-autonomous/libs/flightpath-service-api";
	import { useFarmAndBoundaryCreateMutation } from "@shared/composables/farms";
	import type { Polygon, MultiPolygon } from "geojson";
	import ButtonPrimary from "../ButtonPrimary.vue";
	import ButtonBack from "../ButtonBack.vue";

	onMounted(async () => {
		await nextTick();
		selectMode();
	});

	const errorMessage = ref("");
	const { map } = useMap();
	const { addPaintMode, DrawMode } = useMapDrawing();
	const draw = addPaintMode({
		defaultMode: "simple_select",
	});
	const drawMode = ref(DrawMode.SIMPLE_SELECT);
	const hasSelection = ref(false);
	const nameFieldDialog = ref<InstanceType<typeof Dialog> | null>(null);
	const saveFieldLoading = ref(false);

	const flightPathServiceHttp = useFlightpathServiceHttp();
	const farmAndBoundaryCreateMutation = useFarmAndBoundaryCreateMutation(
		flightPathServiceHttp,
	);
	const emits = defineEmits<{
		fieldCreationModeChanged: [isCreating: boolean];
	}>();

	const formData = reactive({
		fieldName: "",
	});
	const validation = useValidation(
		formData,
		z.object({
			fieldName: z.string().min(1, "Field name is required"),
		}),
	);

	function selectMode() {
		enableMapInteraction();
		draw.changeMode(DrawMode.SIMPLE_SELECT);
		drawMode.value = DrawMode.SIMPLE_SELECT;
		hasSelection.value = false;
	}

	function enableMapInteraction() {
		map.dragPan.enable();
		map.doubleClickZoom.enable();
		map.touchZoomRotate.enable();
	}

	function paintMode() {
		errorMessage.value = "";
		disableMapInteraction();
		drawMode.value = DrawMode.PAINT;
		const featureIds: string[] = draw
			.getAll()
			.features.map(f => f.id)
			.filter((id): id is string => !!id);

		if (featureIds.length === 0) {
			draw.changeMode(DrawMode.PAINT);
			return;
		}
		draw.changeMode(DrawMode.PAINT, { featureIds: featureIds });
	}

	function borderMode() {
		errorMessage.value = "";
		disableMapInteraction();
		drawMode.value = DrawMode.PAINT_BORDER;
		const featureIds: string[] = draw
			.getAll()
			.features.map(f => f.id)
			.filter((id): id is string => !!id);

		if (featureIds.length === 0) {
			draw.changeMode(DrawMode.PAINT_BORDER);
			return;
		}
		draw.changeMode(DrawMode.PAINT_BORDER, {
			featureIds: featureIds,
		});
	}

	function toggleEraserMode() {
		drawMode.value = DrawMode.ERASER;
		const featureIds: string[] = draw
			.getAll()
			.features.map(f => f.id)
			.filter((id): id is string => !!id);
		draw.changeMode("eraser_mode", { featureIds: featureIds });
	}

	function deleteDrawnArea() {
		const selectedFeatures = draw.getSelected().features;
		if (selectedFeatures.length > 0) {
			selectedFeatures.forEach(feature => {
				if (feature.id) draw.delete(feature.id as string);
			});
			return;
		}

		draw.getAll().features.forEach(feature => {
			if (feature.id) draw.delete(feature.id as string);
		});
	}

	function disableMapInteraction() {
		map.dragPan.disable();
		map.doubleClickZoom.disable();
		map.touchZoomRotate.disable();
	}

	function cancelFieldCreation() {
		draw.deleteAll();

		// Disable drawing when not in creation mode
		draw.changeMode("simple_select");
		drawMode.value = DrawMode.SIMPLE_SELECT;

		enableMapInteraction();
		emits("fieldCreationModeChanged", false);
	}

	function saveFieldBoundary() {
		errorMessage.value = "";
		const allFeatures = draw.getAll();

		if (allFeatures.features.length === 0) {
			errorMessage.value = "Error in field boundary";
			return;
		}

		showNameFieldDialog();
	}

	function showNameFieldDialog() {
		nameFieldDialog.value?.show();
	}

	function hideNameFieldDialog() {
		nameFieldDialog.value?.hide();
		formData.fieldName = "";
		validation.reset();
		errorMessage.value = "";
	}

	function handleCancelSave() {
		hideNameFieldDialog();
	}

	async function handleConfirmSave() {
		try {
			saveFieldLoading.value = true;
			errorMessage.value = "";

			if (!validation.validate()) {
				saveFieldLoading.value = false;
				return;
			}

			const allFeatures = draw.getAll();
			if (allFeatures.features.length === 0) {
				errorMessage.value =
					"No field boundary found. Please draw a field boundary first.";
				saveFieldLoading.value = false;
				return;
			}

			if (allFeatures.features.length === 1) {
				await farmAndBoundaryCreateMutation.mutateAsync({
					name: formData.fieldName,
					boundaries: allFeatures.features[0].geometry as Polygon,
				});
			} else {
				const multiPolygon: MultiPolygon = {
					type: "MultiPolygon",
					coordinates: allFeatures.features.map(
						feature => (feature.geometry as Polygon).coordinates,
					),
				};

				await farmAndBoundaryCreateMutation.mutateAsync({
					name: formData.fieldName,
					boundaries: multiPolygon,
				});
			}

			hideNameFieldDialog();
			cancelFieldCreation();
		} catch (error) {
			console.error("Failed to save field:", error);
			errorMessage.value =
				error instanceof Error
					? error.message
					: "Failed to save field. Please try again.";
		} finally {
			saveFieldLoading.value = false;
		}
	}
</script>

<template>
	<div class="space-y-3">
		<!-- Error Message -->
		<div
			v-if="errorMessage"
			class="bg-red-50 border border-red-400 rounded-lg px-3 py-2 text-sm text-red-600"
		>
			{{ errorMessage }}
		</div>

		<!-- Drawing Tools -->
		<div class="flex gap-2 items-center justify-around">
			<button
				class="flex-1 p-2 rounded-lg border transition-colors flex items-center justify-center"
				:class="{
					'bg-green-600 text-white border-green-600':
						drawMode === 'simple_select',
					'bg-white text-grey-700 border-grey-200 hover:bg-green-200 hover:border-green-600':
						drawMode !== 'simple_select',
				}"
				@click="selectMode"
				title="Select Tool"
			>
				<SelectIcon class="!w-6 !h-6" />
			</button>

			<button
				class="flex-1 p-2 rounded-lg border transition-colors flex items-center justify-center"
				:class="{
					'bg-green-600 text-white border-green-600':
						drawMode === 'draw_paint_border_mode',
					'bg-white text-grey-700 border-grey-200 hover:bg-green-200 hover:border-green-600':
						drawMode !== 'draw_paint_border_mode',
				}"
				@click="borderMode"
				title="Border Tool"
			>
				<PenIcon class="!w-6 !h-6" />
			</button>

			<button
				class="flex-1 p-2 rounded-lg border transition-colors flex items-center justify-center"
				:class="{
					'bg-green-600 text-white border-green-600':
						drawMode === 'draw_paint_mode',
					'bg-white text-grey-700 border-grey-200 hover:bg-green-200 hover:border-green-600':
						drawMode !== 'draw_paint_mode',
				}"
				@click="paintMode"
				title="Paint Tool"
			>
				<BrushIcon class="!w-6 !h-6" />
			</button>

			<button
				class="flex-1 p-2 rounded-lg border transition-colors flex items-center justify-center"
				:class="{
					'bg-green-600 text-white border-green-600':
						drawMode === 'eraser_mode',
					'bg-white text-grey-700 border-grey-200 hover:bg-green-200 hover:border-green-600':
						drawMode !== 'eraser_mode',
				}"
				@click="toggleEraserMode"
				title="Erase Tool"
			>
				<EraserIcon class="!w-6 !h-6" />
			</button>

			<button
				class="flex-1 p-2 rounded-lg border border-red-200 text-red-600 hover:border-red-500 hover:bg-red-50 transition-colors flex items-center justify-center"
				@click="deleteDrawnArea"
				title="Delete All"
			>
				<DeleteIcon class="!w-6 !h-6" />
			</button>
		</div>

		<!-- Save Controls -->
		<div class="flex gap-2 border-green-200">
			<ButtonBack
				@click="cancelFieldCreation"
				class="flex-1 px-4 py-2"
			>
				Cancel
			</ButtonBack>
			<ButtonPrimary
				@click="saveFieldBoundary"
				class="flex-1 px-4 py-2 font-medium"
			>
				Save Field
			</ButtonPrimary>
		</div>
	</div>

	<!-- Name Field Dialog -->
	<Dialog
		ref="nameFieldDialog"
		title="Name Your Field"
		confirm-text="Save Field"
		cancel-text="Cancel"
		:loading="saveFieldLoading"
		@confirm="handleConfirmSave"
		@cancel="handleCancelSave"
	>
		<div>
			<label
				for="fieldName"
				class="block text-sm font-medium text-grey-700 mb-2"
			>
				Field Name
			</label>
			<input
				id="fieldName"
				v-model="formData.fieldName"
				type="text"
				required
				class="w-full px-3 py-2 border border-grey-300 rounded-md shadow-sm placeholder-grey-400 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
				:class="{
					'border-red-600': validation.errors.fieldName,
				}"
				placeholder="Enter field name"
				@blur="validation.touch.fieldName"
				:disabled="saveFieldLoading"
			/>
			<div
				v-if="validation.errors.fieldName"
				class="text-red-600 text-sm mt-1"
			>
				{{ validation.errors.fieldName }}
			</div>
		</div>

		<div
			v-if="errorMessage"
			class="bg-red-100 border border-red-400 rounded-lg px-3 py-2 text-sm text-red-600 mt-3"
		>
			{{ errorMessage }}
		</div>
	</Dialog>
</template>
